import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../util/index';
import axios from 'axios';
import debounce from 'lodash.debounce';
import Select from 'react-select';
import SearchIcon from '../icons/SearchIcon.jsx';
import LinkIcon from '../icons/LinkIcon.jsx';
import CloseIcon from '../icons/CloseIcon.jsx';
import {
	insertProductVideoUrlApi,
	externalProductSearchApi,
} from '../util/envConfig.js';

const ProductUrlInsertPage = () => {
	const navigate = useNavigate();
	const [searchResults, setSearchResults] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [showDropdown, setShowDropdown] = useState(false);
	const {
		register,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors },
	} = useForm();
	const storedUserData = localStorage.getItem('userData');
	const userData = storedUserData ? JSON.parse(storedUserData) : null;

	const languages = [
		{ value: 'en', label: 'English' },
		{ value: 'hi', label: 'Hindi' },
		{ value: 'ta', label: 'Tamil' },
		{ value: 'te', label: 'Telugu' },
		{ value: 'kn', label: 'Kannada' },
	];

	const linkTypes = [
		{ value: 'usage', label: 'Usage & Application Guide' },
		{ value: 'testimonial', label: 'Testimonial' },
		{ value: 'expert', label: 'Expert Advice' },
		{ value: 'crop', label: 'Crop-Specific Recommendation' },
		{ value: 'dos', label: "Do's & Don'ts" },
	];

	const onSubmit = async (data) => {
		try {
			const currentDate = new Date().toISOString();
			const selectedProduct = searchResults.find(
				(p) => p.name === data.productName
			);
			if (!selectedProduct?.id) {
				showToast(
					'Please select a valid product from the search results',
					'error'
				);
				return;
			}

			const payload = {
				id: '',
				productName: data.productName || '',
				productVideoUrl: data.productVideoLink || '',
				productId: selectedProduct.id,
				userId: userData.userId,
				language: data.language || 'en',
				createdOn: currentDate,
				updatedOn: currentDate,
				isActive: true,
			};

			const response = await axios.post(insertProductVideoUrlApi, payload, {
				headers: {
					accept: '*/*',
					Authorization: `Bearer ${userData.accessToken}`,
					'Content-Type': 'application/json',
				},
			});

			showToast('URL added successfully!', 'success');
			reset({
				productName: '',
				productVideoLink: '',
				language: '',
				linkType: '',
			});
			setSearchResults([]);
			setShowDropdown(false);
			setSelectedProduct(null);
		} catch (error) {
			console.error('Error submitting form:', error.response || error);
			showToast('Error submitting form. Please try again.', 'error');
		}
	};

	const handleClear = () => {
		reset({
			productName: '',
			productVideoLink: '',
			language: '',
			linkType: '',
		});
		setSearchResults([]);
		setShowDropdown(false);
		setSelectedProduct(null);
	};

	const searchProducts = async (query) => {
		if (!query || query.length < 4) {
			setSearchResults([]);
			setShowDropdown(false);
			return;
		}

		setIsLoading(true);
		try {
			const response = await axios.get(
				`${externalProductSearchApi}?query=${encodeURIComponent(
					query
				)}&pageNum=1&pageSize=5&cursor=DEFAULT&api-version=1.0`,
				{
					headers: {
						accept: '*/*',
						'accept-language': 'en-US,en;q=0.9',
						authorization: `Bearer ${userData?.accessToken}`,
						'user-language': 'en',
					},
				}
			);
			if (!response.data) {
				showToast('No data received from server', 'error');
				setSearchResults([]);
				return;
			}
			const products = Object.values(response.data)
				.map((product) => ({
					id: product?.productId || '',
					name: product?.productName || 'Unknown Product',
					description: product?.productDescription || '',
					imageUrl: product?.productImages?.[0] || '',
				}))
				.filter((product) => product.id && product.name !== 'Unknown Product');

			setSearchResults(products);
			setShowDropdown(true);

			if (products.length === 0) {
				showToast('No matching products found', 'info');
			}
		} catch (error) {
			console.error('Error searching products:', error.response || error);
			setSearchResults([]);
		} finally {
			setIsLoading(false);
		}
	};

	const debouncedSearch = useCallback(
		debounce((query) => {
			searchProducts(query);
		}, 300),
		[]
	);

	// Cleanup debounced function on unmount
	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	const [selectedProduct, setSelectedProduct] = useState(null);

	const handleProductSelect = (product) => {
		if (!product?.name) {
			showToast('Invalid product selected', 'error');
			return;
		}
		setValue('productName', product.name);
		setSelectedProduct(product);
		setShowDropdown(false);
	};

	return (
		<div className="bg-sfBorder">
			<div className="w-full xl:w-[1200px] mx-auto px-4 md:px-0 py-5 md:py-8 flex flex-col">
				<div className="mb-5">
					<button
						className="bg-sfSecondaryColor hover:bg-mediumGreen cursor-pointer text-sm text-white font-medium py-2.5 px-3 rounded-md"
						onClick={() => navigate(-1)}
					>
						Back
					</button>
				</div>

				<div className="min-h-screen flex items-center">
					<div className="w-full md:w-5/12 mx-auto p-5 bg-white shadow-xl rounded-2xl">
						<h3 className="text-2xl font-medium text-black mb-4">
							Product Video Url
						</h3>
						<form onSubmit={handleSubmit(onSubmit)}>
							<div className="mb-4 relative">
								<label
									htmlFor="productName"
									className="block text-sm font-normal text-black mb-1"
								>
									Product Name
									<span style={{ color: 'red' }}>*</span>
								</label>
								<div className="relative">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<SearchIcon />
									</div>
									<input
										type="text"
										id="productName"
										{...register('productName', {
											required: 'Product name is required',
											onChange: (e) => debouncedSearch(e.target.value),
										})}
										className="w-full h-11 pl-10 pr-3 border border-borderColor rounded-md text-sm font-normal text-black focus:ring-2 focus:ring-sfSecondaryColor focus:border-sfSecondaryColor"
										autoComplete="off"
										placeholder="Search for products..."
									/>
									{isLoading && (
										<div className="absolute inset-y-0 right-0 flex items-center pr-3">
											<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-sfSecondaryColor"></div>
										</div>
									)}
								</div>
								{errors.productName && (
									<span className="text-red-500 text-xs">
										{errors.productName.message}
									</span>
								)}
								{showDropdown && (
									<div className="absolute z-10 w-full max-w-[calc(100%-2rem)] left-1/2 -translate-x-1/2 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
										{isLoading ? (
											<div className="p-2 text-center text-gray-500">
												Loading...
											</div>
										) : searchResults.length > 0 ? (
											searchResults.map((product) => (
												<div
													key={product.id}
													className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
													onClick={() => handleProductSelect(product)}
												>
													{product.imageUrl && (
														<img
															src={product.imageUrl}
															alt={product.name}
															className="w-8 h-8 object-cover rounded mr-3 flex-shrink-0"
														/>
													)}
													<div className="min-w-0 flex-1">
														<div className="text-sm font-medium text-gray-900 truncate">
															{product.name}
														</div>
													</div>
												</div>
											))
										) : (
											<div className="p-2 text-center text-gray-500">
												No products found
											</div>
										)}
									</div>
								)}
							</div>

							<div className="mb-4">
								<label
									htmlFor="language"
									className="block text-sm font-normal text-black mb-1"
								>
									Language
									<span style={{ color: 'red' }}>*</span>
								</label>
								<Select
									id="language"
									options={languages}
									value={
										languages.find(
											(option) => option.value === watch('language')
										) || null
									}
									onChange={(selectedOption) => {
										setValue(
											'language',
											selectedOption ? selectedOption.value : ''
										);
									}}
									isClearable={true}
									className="w-full text-sm font-normal text-black"
									classNamePrefix="react-select"
									placeholder="Select Language"
								/>
								{errors.language && (
									<span className="text-red-500 text-xs">
										{errors.language.message}
									</span>
								)}
							</div>

							<div className="mb-4">
								<label
									htmlFor="linkType"
									className="block text-sm font-normal text-black mb-1"
								>
									Link Type
									<span style={{ color: 'red' }}>*</span>
								</label>
								<Select
									id="linkType"
									options={linkTypes}
									value={
										linkTypes.find(
											(option) => option.value === watch('linkType')
										) || null
									}
									onChange={(selectedOption) => {
										setValue(
											'linkType',
											selectedOption ? selectedOption.value : ''
										);
									}}
									isClearable={true}
									className="w-full text-sm font-normal text-black"
									classNamePrefix="react-select"
									placeholder="Select Link Type"
								/>
								{errors.linkType && (
									<span className="text-red-500 text-xs">
										{errors.linkType.message}
									</span>
								)}
							</div>

							<div className="mb-4">
								<label
									htmlFor="productVideoLink"
									className="block text-sm font-normal text-black mb-1"
								>
									Url
									<span style={{ color: 'red' }}>*</span>
								</label>
								<div className="relative">
									<input
										type="text"
										id="productVideoLink"
										{...register('productVideoLink', {
											required: 'Product link is required',
											validate: {
												validUrl: (value) => {
													if (!value) return true;
													try {
														new URL(
															value.startsWith('http')
																? value
																: `https://${value}`
														);
														return true;
													} catch {
														return 'Please enter a valid URL (e.g., www.google.com or https://google.com)';
													}
												},
											},
											onChange: (e) => {
												let value = e.target.value;
												if (
													!value ||
													e.nativeEvent.inputType === 'deleteContentBackward'
												) {
													return;
												}
												if (!value.match(/^https?:\/\//)) {
													value = value.trim().replace(/^www\./, '');
													if (value) {
														value = `https://${value}`;
														e.target.value = value;
													}
												}
											},
										})}
										className="w-full h-11 py-1 px-3 pr-20 border border-borderColor rounded-md text-sm font-normal text-sfSecondaryColor hover:text-sfPrimaryColor transition-colors duration-200"
										placeholder="Enter URL (e.g., www.google.com)"
									/>
									{watch('productVideoLink') && (
										<button
											type="button"
											onClick={() => setValue('productVideoLink', '')}
											className="absolute right-10 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
											title="Clear URL"
										>
											<CloseIcon />
										</button>
									)}
									{!errors.productVideoLink && (
										<a
											href={watch('productVideoLink')}
											target="_blank"
											rel="noopener noreferrer"
											className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
											title="Open link in new tab"
											onClick={(e) => {
												if (!watch('productVideoLink')) {
													e.preventDefault();
												}
											}}
										>
											<LinkIcon />
										</a>
									)}
								</div>
								{errors.productVideoLink && (
									<span className="text-red-500 text-xs">
										{errors.productVideoLink.message}
									</span>
								)}
								{watch('productVideoLink') && !errors.productVideoLink && (
									<div className="mt-1">
										<a
											href={watch('productVideoLink')}
											target="_blank"
											rel="noopener noreferrer"
											className="text-xs text-sfSecondaryColor hover:text-sfPrimaryColor hover:underline transition-colors duration-200"
										>
											{watch('productVideoLink')}
										</a>
									</div>
								)}
							</div>

							<div className="flex justify-end">
								<button
									type="button"
									onClick={handleClear}
									className="w-full md:w-1/2 bg-disableText hover:bg-opacity-90 text-sm font-medium text-white py-2.5 px-3 rounded-md mr-2"
								>
									Clear
								</button>
								<button
									type="submit"
									className="w-full md:w-1/2 bg-footerColor2 hover:bg-green500 text-sm font-medium text-white py-2.5 px-3 rounded-md ml-2"
								>
									Submit
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProductUrlInsertPage;
